import type { FC, PropsWithChildren } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import TeamAvatar from 'app/components/TeamAvatar';
import { getTeamNameAndAcronym } from 'app/utils/entities/match_data';

import style from './style.module.scss';

interface Props extends ClassNameProp, PropsWithChildren {
  orientation?: Orientation;
  team: OpponentBase | undefined;
  namePosition?: 'side' | 'bottom';
  logoStyle?: string;
  teamNamesClassName?: string;
  useAcronym?: boolean;
}

const Container: FC<ClassNameProp & PropsWithChildren> = ({ children, className }) => (
  <div className={classNames(style.teamNamesContainer, className)}>{children}</div>
);

const TeamName: FC<Props> & { Container: typeof Container } = ({
  orientation = 'normal',
  team,
  namePosition = 'side',
  logoStyle,
  className,
  teamNamesClassName,
  useAcronym = false
}) => {
  const { t } = useTranslation();

  const teamNameContainerClassname = classNames(
    style.teamNameLogo,
    {
      [style.reversed]: orientation === 'reversed',
      [style.bottomName]: namePosition === 'bottom'
    },
    className
  );

  const TeamNameClassname = classNames(
    style.teamName,
    {
      [style.bottomName]: namePosition === 'bottom'
    },
    teamNamesClassName
  );

  const { name, acronym } = getTeamNameAndAcronym(team, t);

  return (
    <div className={teamNameContainerClassname}>
      <TeamAvatar team={team} className={logoStyle} withBackground noAlt />
      <div className={TeamNameClassname}>{useAcronym ? acronym : name}</div>
    </div>
  );
};

Container.displayName = 'Container';

TeamName.displayName = 'TeamName';
TeamName.Container = Container;

export default TeamName;
